import React, { useState, ReactElement, MutableRefObject } from 'react';

import { TextField, Button, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';

import { DialogTitle, DialogContent, DialogActions, Dialog } from '../../AlmeDialog/AlmeDialog';
import { AlmeSurveyConfig, RadioButtonColor } from './AlmeSurveyConfig';
import { FireSurveyAppEvent, SurveyDialogDisplayText } from "./AlmeSurvey";
import { useIntl } from 'react-intl';
import { useFocusStyles } from '../../FocusVisibleStyle';

export interface AlmeSurveyDialogProps {
    onSubmitSurvey: FireSurveyAppEvent;
    setIsDialogOpen: (isOpen: boolean) => void;
    isOpen: boolean;
    almeConfig: AlmeSurveyConfig;
    entryId: string;
    surveyDialogDisplayText: SurveyDialogDisplayText;
    scrollToResponse?: MutableRefObject<boolean>;
}

const AlmeSurveyDialog: React.FC<AlmeSurveyDialogProps> = (props: AlmeSurveyDialogProps): ReactElement => {
    const intl = useIntl();
    const Focusclasses = useFocusStyles();
    const firstRadioButton = props.almeConfig.survey.dialog.radioButtonOptions?.find(x => x !== undefined) ?? '';
    const [surveyInput, setSurveyInput] = useState<string>('');
    const [surveyReason, setSurveyReason] = useState<string>(firstRadioButton);

    const textChanged = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const inputText = event.target.value;
        setSurveyInput(inputText);
    };

    const onSubmitInput = (hasReason: boolean) => async (): Promise<void> => {
        if (props.scrollToResponse)
            props.scrollToResponse.current = true;

        await props.onSubmitSurvey(
            props.entryId,
            props.almeConfig.survey.dialog.submitLinkConfig?.AppeventName ?? "GradingComplete",
            props.almeConfig.survey.dialog.submitLinkConfig?.parameters.grade ?? 0,
            props.almeConfig.survey.dialog.submitLinkConfig?.parameters.SurveyUnitLabel ?? "",
            hasReason ? surveyInput.trim() : '',
            props.almeConfig.survey.dialog.submitLinkConfig?.parameters.gradingQueue,
            hasReason ? surveyReason : '',
            props.almeConfig.survey.dialog.submitLinkConfig?.parameters.gradingType);
        setSurveyInput('');
    };

    const radioGroup = getRadioGroup(surveyReason, firstRadioButton, props.almeConfig.survey.dialog.radioButtonColor, setSurveyReason,
        props.almeConfig.survey?.dialog?.radioButtonOptions?.length, props.almeConfig.survey?.dialog?.radioButtonOptions);

    const freeFormText = props.almeConfig.survey?.dialog?.freeformTextMaxLength !== 0
        ? <TextField
            id="alme-survey-dialog-input"
            value={surveyInput}
            multiline
            label={props.surveyDialogDisplayText.bodyText}
            variant='outlined'
            fullWidth
            rows='5'
            onChange={textChanged}
            inputProps={{ maxLength: props.almeConfig.survey.dialog.freeformTextMaxLength }}
        />
        : <></>;

    return (
        <Dialog
            id="alme-survey-dialog"
            aria-label={
                intl.formatMessage({
                    id: "survey.dialogAriaLabel",
                    defaultMessage: 'Default survey dialog aria label',
                    description: 'AlmeSurvey dialog aria label',
                })}
            aria-describedby="alme-survey-dialog-description"
            open={props.isOpen}
            disableEnforceFocus
            setIsDialogOpen={props.setIsDialogOpen}
            onClose={onSubmitInput(false)}
            fullWidth
        >
            <DialogTitle id="alme-survey-dialog-title" almeConfig={props.almeConfig.survey} onClose={onSubmitInput(false)}>
                {props.surveyDialogDisplayText.headerText}
            </DialogTitle>
            <DialogContent dividers>
                {radioGroup}
                {freeFormText}
            </DialogContent>
            <DialogActions>
                <Button
                    className={Focusclasses.visibleFocus}
                    variant={props.almeConfig.survey.dialog.styles.buttons.variant}
                    color={props.almeConfig.survey.dialog.styles.buttons.color}
                    onClick={onSubmitInput(false)}
                >
                    {props.surveyDialogDisplayText.buttons.declineButtonText}
                </Button>
                <Button
                    className={Focusclasses.visibleFocus}
                    variant={props.almeConfig.survey.dialog.styles.buttons.variant}
                    color={props.almeConfig.survey.dialog.styles.buttons.color}
                    onClick={onSubmitInput(true)}
                >
                    {props.surveyDialogDisplayText.buttons.acceptButtonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AlmeSurveyDialog;


function getRadioGroup(surveyReason: string, firstRadioButton: string, radioButtonColor: RadioButtonColor,
    setSurveyReason: (value: React.SetStateAction<string>) => void, radioButtonLength?: number, radioButtonOptions?: string[]): JSX.Element {
    return (radioButtonLength ?? 0) > 0
        ? <RadioGroup
            aria-label="feedback category"
            value={surveyReason}
            defaultValue={firstRadioButton}
            onChange={(event, value) => { setSurveyReason(value); }}
        >
            {radioButtonOptions?.map((radioValue, index) => (
                <FormControlLabel key={index} value={radioValue} control={<Radio color={radioButtonColor} />} label={radioValue} />
            ))}
        </RadioGroup>
        : <></>;
}
